import { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./CallFeaturesModal.css";
import * as BoxesActions from "../../src/actions/EpikBoxes";
import { connect } from "react-redux";
import BulkSelectPortsModal from "./BulkSelectionModal";
import { toast } from "react-toastify";
import InfoPopupCallFeature from "../../src/components/callFeatureInfoPop";
import { TOOLTIPS_TEXT } from "../../src/lib/constants";
import Toggle from "../../src/components/Toggle/index";
import { isEscene } from "../../src/lib/utils";
import { NUMBER_TYPES_LABEL } from "../lib/constants";
import CallFeaturesWarningModal from "./CallFeaturesWarningModal";
import { getPermissionForComponent, canView, canWrite } from "../utils/permissionsUtils";


const isValidPhone = (s) => sanitizeDigits(s).length === 10;
const parseBlockedNumbers = (s) =>
  String(s || "")
    .split(",")
    .map((p) => sanitizeDigits(p))
    .filter(Boolean);

const FORWARD_TO_ASSIGNED_PORT_ERROR =
  "A port cannot forward calls to the same destination number that is already assigned to that port.";

/** Compare two phone numbers in a format-agnostic way (e.g. 987654321 vs +1987654321). */
function phoneNumbersMatch(a, b) {
  const da = sanitizeDigits(a);
  const db = sanitizeDigits(b);
  if (!da || !db) return false;
  if (da === db) return true;
  if (da.length >= db.length) return da.endsWith(db);
  return db.endsWith(da);
}
const DIGITS_ONLY = /\D+/g;
const sanitizeDigits = (s) => String(s || "").replace(DIGITS_ONLY, "");
const digitsOnly10 = (s) => String(s || "").replace(DIGITS_ONLY, "").slice(0, 10);
const isValidPhone10 = (s) => digitsOnly10(s).length === 10;
function PhoneInput({ value, onChange, placeholder = "Enter 10-digit number", disabled = false }) {
  const v = digitsOnly10(value);

  return (
    <input
      className="cf-input"
      inputMode="numeric"
      pattern="\d*"
      maxLength={10}
      value={v}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(digitsOnly10(e.target.value))}
      onKeyDown={(e) => {
        const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
        if (e.ctrlKey || e.metaKey) {
          return;
        }
        if (allowed.includes(e.key)) return;
        if (/^[0-9]$/.test(e.key)) return;
        e.preventDefault();
      }}
    />
  );
}

function buildTabsAndGrid(boxDetails, oobData) {
  const collected = [];
  const collectFromDevice = (deviceData, sourceType) => {
    (deviceData?.Obis || []).forEach((obi) => {
      const p1 = obi?.port1;
      if (p1 && p1.assignedNumberRef?.number) {
        const num = p1.boxPortNumber ?? collected.length + 1;
        collected.push({
          label: `${num}`,
          source: sourceType,
          boxId: deviceData._id,
          obiId: obi._id,
          boxPortNumber: num,
          macAddress: obi.macAddress,
          deviceId: obi.deviceId,
          numberId: p1.assignedNumberRef?._id || p1.assignedNumberRef?.id || null,
        });
      }
      const p2 = obi?.port2;
      if (p2 && p2.assignedNumberRef?.number) {
        const num = p2.boxPortNumber ?? collected.length + 1;
        collected.push({
          label: `${num}`,
          source: sourceType,
          boxId: deviceData._id,
          obiId: obi._id,
          boxPortNumber: num,
          macAddress: obi.macAddress,
          deviceId: obi.deviceId,
          numberId: p2.assignedNumberRef?._id || p2.assignedNumberRef?.id || null,
        });
      }
    });
    (oobData?.Obis || []).forEach((obi) => {
      if (obi?.port1 && obi.port1.assignedNumberRef?.number) {
        collected.push({
          label: `${obi.port1.boxPortNumber}`,
          source: "oob",
          boxId: boxDetails._id,
          obiId: obi._id,
          boxPortNumber: obi.port1.boxPortNumber,
          macAddress: obi.macAddress,
          deviceId: obi.deviceId,
          numberId: obi.port1.assignedNumberRef?._id || null,
        });
      }
      if (obi?.port2 && obi.port2.assignedNumberRef?.number) {
        collected.push({
          label: `${obi.port2.boxPortNumber}`,
          source: "oob",
          boxId: boxDetails._id,
          obiId: obi._id,
          boxPortNumber: obi.port2.boxPortNumber,
          macAddress: obi.macAddress,
          deviceId: obi.deviceId,
          numberId: obi.port2.assignedNumberRef?._id || null,
        });
      }
    });
  };
  collectFromDevice(boxDetails, "box");
  const headerTabs = [...collected.slice(0, 8).map((p) => p.label)];
  const gridPorts = collected.length > 8 ? collected.slice(8).map((p) => p.label) : [];
  return { headerTabs, gridPorts, ports: collected };
}

const SECONDS_OPTS = Array.from({ length: 55 }, (_, i) => i + 6);
const cx = (...c) => c.filter(Boolean).join(" ");

function Pill({ active, onClick, children }) {
  return (
    <button className={cx("cf-pill", active && "active")} onClick={onClick}>
      {children}
    </button>
  );
}

function SecondsDropdown({ value, onChange, disabled = false }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const MENU_MAX_HEIGHT = 240;

  const updatePos = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const openUp =
      r.bottom + MENU_MAX_HEIGHT > window.innerHeight && r.top > MENU_MAX_HEIGHT;
    const top = window.scrollY + (openUp ? r.top - MENU_MAX_HEIGHT - 6 : r.bottom + 6);
    setPos({ top, left: window.scrollX + r.left, width: r.width });
  };
  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    const onWin = () => updatePos();
    const onClick = (e) => {
      if (!btnRef.current) return;
      if (e.target === btnRef.current || btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const menu = open
    ? createPortal(
        <div
          className="cf-dd-menu"
          style={{
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: MENU_MAX_HEIGHT,
          }}
        >
          <ul className="cf-dd-list" role="listbox" aria-label="Seconds">
            {SECONDS_OPTS.map((n) => (
              <li
                key={n}
                role="option"
                aria-selected={n === value}
                className={cx("cf-dd-item", n === value && "selected")}
                onClick={() => {
                  onChange(n);
                  setOpen(false);
                }}
              >
                {n}
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )
    : null;
  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className="cf-dd-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        {value}
        <span className="cf-dd-caret" aria-hidden>
          ▾
        </span>
      </button>
      {menu}
    </>
  );
}

const defaultPortState = () => ({
  busyOn: false,
  busyExpand: false,
  busyRoute: "voicemail",
  busyPhone: "",
  naOn: false,
  naExpand: false,
  naSeconds: 18,
  naRoute: "voicemail",
  naPhone: "",
  alwaysOn: false,
  alwaysExpand: false,
  alwaysRoute: "voicemail",
  alwaysPhone: "",
  remoteOn: false,
  remoteExpand: false,
  remotePhone: "",
  dndOn: false,
  threeWayOn: false,
  callWaiting: false,
  CallTransferEnable: false,
  acrOn: false,
  acrExpand: false,
  blockOn: false,
  blockExpand: false,
  blockInput: "",
  blockList: [],
  isEsceneDevice: false,
  macAddress: "",
});

function CallFeaturesModal({
  open,
  onClose,
  boxDetails,
  getSalveboxOob,
  updateDeviceFeatures,
  updatePortFeature,
  bulkUpdateCallFeaturePorts,
  user,
}) {
  const [activeTab, setActiveTab] = useState("");
  const [selectedGridPort, setSelectedGridPort] = useState(null);
  const [portState, setPortState] = useState({});
  const [saving, setSaving] = useState({});
  const [oobError, setOobError] = useState("");
  const [oobData, setOobData] = useState(null);
  const token = localStorage.getItem("jwtToken") || null;
  const [showBulkModal, setShowBulkModal] = useState(false);
  // const [isEsceneDevice, setIsEsceneDevice] = useState(false);

  const perm = useMemo(() => {
    const group = user?.permissionsGroup;
    return {
      threeWayCalling: getPermissionForComponent("threeWayCalling", group),
      callTransfer: getPermissionForComponent("callTransfer", group),
      callWaiting: getPermissionForComponent("callWaiting", group),

      callForwardBusy: getPermissionForComponent("callForwardBusy", group),
      callForwardNoAnswer: getPermissionForComponent("callForwardNoAnswer", group),
      callForwardAlways: getPermissionForComponent("callForwardAlways", group),
      remoteCallForward: getPermissionForComponent("remoteCallForward", group),

      callRejection: getPermissionForComponent("callRejection", group),
      callBlocking: getPermissionForComponent("callBlocking", group),
      doNotDisturb: getPermissionForComponent("doNotDisturb", group),
    };
  }, [user?.permissionsGroup]);

  // const canViewKey = (k) => canView(perm[k]);
  // const canWriteKey = (k) => canWrite(perm[k]);
  const canViewKey = (k) => canView(perm[k], k);
const canWriteKey = (k) => canWrite(perm[k], k);

  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null, // 'callWaiting' | 'remote' | 'bulk'
    payload: null,
  });

  const { headerTabs: topTabs, gridPorts, ports: portsMeta } = useMemo(
    () => buildTabsAndGrid(boxDetails, oobData),
    [boxDetails, oobData]
  );

  const portMetaByLabel = useMemo(() => {
    const m = new Map();
    for (const p of portsMeta) m.set(p.label, p);
    return m;
  }, [portsMeta]);

  useEffect(() => {
    const valid = topTabs.includes(activeTab) || /^Port \d+$/.test(activeTab);
    if (!valid) setActiveTab(topTabs[0] || activeTab);
  }, [topTabs, activeTab]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setOobError("");
        const bid = boxDetails?.slaveBoxes?.[0]?._id;
        if (!bid) return;
        const data = await getSalveboxOob(token, bid);
        setOobData(data);
      } catch (e) {
        setOobError(e?.message || String(e));
      }
    })();
  }, [open, boxDetails, getSalveboxOob, token]);

  useEffect(() => {
    if (!oobData && !boxDetails) return;
    const newPortState = {};
    const initPortFromData = (portData, label,obi) => {
      if (!portData) return;
      const remote = portData.assignedNumberRef || {};
      const acrEnabled = !!(portData.callRejection || portData.anonymousCallRejection);
      const cb = portData?.callBlocking;
      const blockingEnabled =
        typeof cb === "object"
          ? !!cb.enabled
          : !!(
              (portData.callBlockingSettings && portData.callBlockingSettings.enabled) ||
              portData.callBlocking
            );

      let blockingNumbers = [];
      if (cb && typeof cb === "object") {
        blockingNumbers = parseBlockedNumbers(cb.numbers || "");
      } else if (portData.callBlockingSettings && Array.isArray(portData.callBlockingSettings.numbers)) {
        blockingNumbers = portData.callBlockingSettings.numbers
          .map((n) => (typeof n === "string" ? n : n.number))
          .filter(Boolean)
          .map((n) => sanitizeDigits(n));
      }

      newPortState[label] = {
        busyOn: !!(portData.callForwardBusy && portData.callForwardBusy.enabled),
        busyExpand: !!(portData.callForwardBusy && portData.callForwardBusy.enabled),
        busyRoute: portData.callForwardBusy?.route || "voicemail",
        busyPhone: portData.callForwardBusy?.phoneNumber || "",
        naOn: !!(portData.callForwardNoAnswer && portData.callForwardNoAnswer.enabled),
        naExpand: !!(portData.callForwardNoAnswer && portData.callForwardNoAnswer.enabled),
        naSeconds: portData.callForwardNoAnswer?.ringSeconds || 18,
        naRoute: portData.callForwardNoAnswer?.route || "voicemail",
        naPhone: portData.callForwardNoAnswer?.phoneNumber || "",
        alwaysOn: !!(portData.callForwardAlways && portData.callForwardAlways.enabled),
        alwaysExpand: !!(portData.callForwardAlways && portData.callForwardAlways.enabled),
        alwaysRoute: portData.callForwardAlways?.route || "voicemail",
        alwaysPhone: portData.callForwardAlways?.phoneNumber || "",
        remoteOn: !!remote.isForwarded,
        remoteExpand: !!remote.isForwarded,
        remotePhone:
          remote.forwardNumbers && remote.forwardNumbers[0] ? remote.forwardNumbers[0] : "",
        dndOn: !!portData.doNotDisturb,
        threeWayOn: !!portData.conferenceCall,
        callWaiting: !!portData.callWaiting,
        CallTransferEnable: !!portData.CallTransferEnable,
        selectNumber: remote,
        acrOn: acrEnabled,
        acrExpand: acrEnabled,
        blockOn: blockingEnabled,
        blockExpand: blockingEnabled,
        blockInput: "",
        blockList: blockingNumbers,
        isEsceneDevice: isEscene(obi?.macAddress),
        macAddress: obi?.macAddress || "",
      };
    };
    (boxDetails?.Obis || []).forEach((obi) => {
      if (obi?.port1 && obi.port1.assignedNumberRef?.number) {
        initPortFromData(obi.port1, String(obi.port1.boxPortNumber), obi);
      }
      if (obi?.port2 && obi.port2.assignedNumberRef?.number) {
        initPortFromData(obi.port2, String(obi.port2.boxPortNumber), obi);
      }
    });
    
    (oobData?.Obis || []).forEach((obi) => {
      if (obi?.port1 && obi.port1.assignedNumberRef?.number) {
        initPortFromData(obi.port1, String(obi.port1.boxPortNumber), obi);
      }
      if (obi?.port2 && obi.port2.assignedNumberRef?.number) {
        initPortFromData(obi.port2, String(obi.port2.boxPortNumber), obi);
      }
    });
    setPortState(newPortState);
  }, [boxDetails, oobData]);

  if (!open) return null;

  const isPortView = activeTab.startsWith("Port ") || /^\d+$/.test(String(activeTab));
  const currentPort = isPortView ? activeTab : null;

  const selectTab = (tab) => {
    setActiveTab(tab);
    setSelectedGridPort(null);
  };

  const selectGridPort = (p) => {
    setActiveTab(p);
    setSelectedGridPort(p);
  };

  const getP = (p) => (p ? portState[p] ?? defaultPortState() : defaultPortState());

  const setP = (p, updater) =>
    setPortState((s) => {
      const cur = s[p] ?? defaultPortState();
      const next = typeof updater === "function" ? updater(cur) : updater;
      return { ...s, [p]: next };
    });

  //Call Blocking: Add validation + toast if invalid
  const addBlockNumber = () => {
    if (!currentPort) return;
    const cur = getP(currentPort);
    const nums = parseBlockedNumbers(cur.blockInput || "");
    if (nums.length === 0) {
      toast.error("Please enter at least one number.");
      return;
    }
    const invalid = nums.find((n) => String(n).length <= 10);
    if (invalid) {
      toast.error("Each blocked number must be at least 10 digits.");
      return;
    }
      setP(currentPort, (s) => {
        const existing = s.blockList || [];
        const nextList = [...existing];
        nums.forEach((n) => {
          if (!nextList.includes(n)) nextList.push(n);
        });
        return { ...s, blockList: nextList, blockInput: "" };
      });
    };

  const removeBlockNumber = (num) => {
    if (!currentPort) return;
    setP(currentPort, (s) => ({
      ...s,
      blockList: (s.blockList || []).filter((n) => n !== num),
    }));
  };

  const handleBlockInputChange = (e) => {
    if (!currentPort) return;
    setP(currentPort, (s) => ({ ...s, blockInput: e.target.value }));
  };

  const handleBlockInputKeyDown = (e) => {
    const allowed = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Enter",
      ",",
      " ",
      "Spacebar",
      "Home",
      "End",
    ];
  
    if (e.ctrlKey || e.metaKey) {
      return;
    }
  
    if (allowed.includes(e.key)) {
      if (e.key === "Enter") {
        e.preventDefault();
        addBlockNumber();
      }
      return;
    }
    if (/^[0-9]$/.test(e.key)) return;
    e.preventDefault();
  };

  const persistDisableCallBlocking = async (label) => {
    const meta = portMetaByLabel.get(label);
    if (!meta) throw new Error("Port data not found");
    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, {
      callBlocking: { enabled: false, numbers: "" },
    });
  };

  const handleSaveCallBlocking = async () => {
    if (!currentPort) return;
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.blockOn) {
      const invalid = (s.blockList || []).find((n) => String(n).length < 9);
      if (invalid) {
        toast.error("Each blocked number must be at least 9 digits.");
        return;
      }
    }

    const numbersStr = s.blockOn ? (s.blockList || []).map(String).join(",") : "";

    const body = {
      callBlocking: {
        enabled: !!s.blockOn,
        numbers: numbersStr,
      },
    };

    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, body);
    toast.success("Call Blocking settings saved");
  };

  const buildBusyPayload = (s) => {
    if (!s.busyOn)
      return {
        callForwardBusy: { enabled: false, route: "voicemail", phoneNumber: "" },
      };
    const route = s.busyRoute === "phone" ? "phone" : "voicemail";
    const phoneNumber = route === "phone" ? sanitizeDigits(s.busyPhone) : "";
    return { callForwardBusy: { enabled: true, route, phoneNumber } };
  };

  const buildNoAnswerPayload = (s) => {
    if (!s.naOn)
      return {
        callForwardNoAnswer: {
          enabled: false,
          ringSeconds: 18,
          route: "voicemail",
          phoneNumber: "",
        },
      };
    const route = s.naRoute === "phone" ? "phone" : "voicemail";
    const phoneNumber = route === "phone" ? sanitizeDigits(s.naPhone) : "";
    const ringSeconds = Number(s.naSeconds || 18);
    return {
      callForwardNoAnswer: { enabled: true, ringSeconds, route, phoneNumber },
    };
  };

  const buildAlwaysPayload = (s) => {
    if (!s.alwaysOn)
      return {
        callForwardAlways: { enabled: false, route: "voicemail", phoneNumber: "" },
      };
    const route = s.alwaysRoute === "phone" ? "phone" : "voicemail";
    const phoneNumber = route === "phone" ? sanitizeDigits(s.alwaysPhone) : "";
    return { callForwardAlways: { enabled: true, route, phoneNumber } };
  };
  const buildRemotePayload = (s) => {
    if (!s.remoteOn) {
      return {
        remoteCallForwarding: {
          isForwarded: false,
          forwardNumbers: [],
        },
      };
    }
    const num = sanitizeDigits(s.remotePhone);
    return {
      remoteCallForwarding: {
        isForwarded: true,
        forwardNumbers: num ? [num] : [],
      },
    };
  };

  const withSaving = async (key, fn) => {
    if (saving[key]) return;
    try {
      setSaving((m) => ({ ...m, [key]: true }));
      await fn();
    } catch (err) {
      toast.error(err?.message || "Operation failed");
    } finally {
      setSaving((m) => ({ ...m, [key]: false }));
    }
  };
  // Validation flags used for disabling buttons
  const canSaveBusy =
    isPortView &&
    (!getP(currentPort).busyOn ||
      getP(currentPort).busyRoute === "voicemail" ||
      (getP(currentPort).busyRoute === "phone" && isValidPhone(getP(currentPort).busyPhone, 10)));

  const canSaveNA =
    isPortView &&
    (!getP(currentPort).naOn ||
      getP(currentPort).naRoute === "voicemail" ||
      (getP(currentPort).naRoute === "phone" && isValidPhone(getP(currentPort).naPhone, 10)));

  const canSaveAlways =
    isPortView &&
    (!getP(currentPort).alwaysOn ||
      getP(currentPort).alwaysRoute === "voicemail" ||
      (getP(currentPort).alwaysRoute === "phone" && isValidPhone(getP(currentPort).alwaysPhone, 10)));

  const canSaveRemote =
    isPortView && (!getP(currentPort).remoteOn || isValidPhone(getP(currentPort).remotePhone, 10));

  const currentPortState = getP(currentPort);
  const currentPortIsEscene = !!currentPortState?.isEsceneDevice;

  //Save handlers: show toast if invalid AND do nothing
  const handleSaveBusy = async () => {
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.busyOn && s.busyRoute === "phone" && !isValidPhone(s.busyPhone, 10)) {
      toast.error("Busy forwarding number must be exactly 10 digits");
      return;
    }
    if (
      s.busyOn &&
      s.busyRoute === "phone" &&
      s.busyPhone &&
      phoneNumbersMatch(s.busyPhone, currentPortState.selectNumber?.number)
    ) {
      toast.error(FORWARD_TO_ASSIGNED_PORT_ERROR);
      return;
    }
    const payload = buildBusyPayload(s);
    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, payload);
    toast.success(`Call Forward Busy saved for ${currentPort}`);
  };

  const handleSaveNoAnswer = async () => {
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.naOn && s.naRoute === "phone" && !isValidPhone(s.naPhone, 10)) {
      toast.error("No-Answer forwarding number must be exactly 10 digits");
      return;
    }
    if (
      s.naOn &&
      s.naRoute === "phone" &&
      s.naPhone &&
      phoneNumbersMatch(s.naPhone, currentPortState.selectNumber?.number)
    ) {
      toast.error(FORWARD_TO_ASSIGNED_PORT_ERROR);
      return;
    }
    const payload = buildNoAnswerPayload(s);
    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, payload);
    toast.success(`Call Forward No-Answer saved for ${currentPort}`);
  };

  const handleSaveAlways = async () => {
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.alwaysOn && s.alwaysRoute === "phone" && !isValidPhone(s.alwaysPhone, 10)) {
      toast.error("Always forwarding number must be exactly 10 digits");
      return;
    }
    if (
      s.alwaysOn &&
      s.alwaysRoute === "phone" &&
      s.alwaysPhone &&
      phoneNumbersMatch(s.alwaysPhone, currentPortState.selectNumber?.number)
    ) {
      toast.error(FORWARD_TO_ASSIGNED_PORT_ERROR);
      return;
    }
    const payload = buildAlwaysPayload(s);
    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, payload);
    toast.success(`Call Forward Always saved for ${currentPort}`);
  };

  const handleSaveRemote = () => {
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.remoteOn && !isValidPhone(s.remotePhone, 10)) {
      toast.error("Remote call forwarding number must be exactly 10 digits");
      return;
    }
    if (s.remoteOn && s.remotePhone && phoneNumbersMatch(s.remotePhone, currentPortState.selectNumber?.number)) {
      toast.error(FORWARD_TO_ASSIGNED_PORT_ERROR);
      return;
    }
    setConfirmState({
      open: true,
      action: "remote",
      payload: {
        portLabel: currentPort,
      },
    });
  };

  const saveSimpleToggle = async (label, key, value) => {
    const meta = portMetaByLabel.get(label);
    if (!meta) throw new Error("Port data not found");
    const resUpdate = await updatePortFeature(
      token,
      meta.obiId,
      meta.boxId,
      meta.boxPortNumber,
      meta.deviceId,
      {
        [key]: !!value,
      }
    );
    const nice = key === "doNotDisturb" ? "Do Not Disturb" : "3 Way Calling";
    toast.success(`${nice} ${value ? "enabled" : "disabled"} for ${label}`);
    return resUpdate;
  };

  const closeConfirm = () =>
    setConfirmState({
      open: false,
      action: null,
      payload: null,
    });

  const handleConfirmWarning = async () => {
    const { action, payload } = confirmState;

    try {
      if (action === "callWaiting" && payload) {
        const { portLabel, nextValue } = payload;
        const meta = portMetaByLabel.get(portLabel);
        if (!meta) throw new Error("Port data not found");

        await updatePortFeature(
          token,
          meta.obiId,
          meta.boxId,
          meta.boxPortNumber,
          meta.deviceId,
          { callWaiting: !!nextValue }
        );
        toast.success(
          `Call Waiting ${nextValue ? "enabled" : "disabled"} for ${portLabel}`
        );
      }

      if (action === "remote" && payload) {
        const { portLabel } = payload;
        const meta = portMetaByLabel.get(portLabel);
        if (!meta) throw new Error("Port data not found");
        const state = getP(portLabel);

        if (state.remoteOn && !isValidPhone(state.remotePhone, 10)) {
          toast.error("Remote call forwarding number must be at least 10 digits");
          closeConfirm();
          return;
        }

        if (state.remoteOn && state.remotePhone && phoneNumbersMatch(state.remotePhone, state.selectNumber?.number)) {
          toast.error(FORWARD_TO_ASSIGNED_PORT_ERROR);
          closeConfirm();
          return;
        }

        const remotePayload = buildRemotePayload(state);

        await withSaving(`${portLabel}:remote`, async () => {
          await updatePortFeature(
            token,
            meta.obiId,
            meta.boxId,
            meta.boxPortNumber,
            meta.deviceId,
            remotePayload
          );
        });

        toast.success(
          `Remote Call Forwarding ${
            state.remoteOn ? "saved" : "disabled"
          } for ${portLabel}`
        );
      }

      if (action === "bulk" && payload) {
        const { selectedPorts, updateData } = payload;
        const result = await bulkUpdateCallFeaturePorts(
          token,
          boxDetails._id,
          selectedPorts,
          updateData
        );

        if (result?.errors?.length > 0) {
          toast.error(
            `Bulk update completed (some failed). Succeeded: ${
              result?.summary?.succeeded ?? 0
            }, Failed: ${result?.summary?.failed ?? 0}`
          );
        } else {
          toast.success(
            `All ${
              result?.summary?.succeeded ?? selectedPorts.length
            } selected port(s) updated successfully`
          );
        }
        onClose && onClose();
      }
    } catch (e) {
      toast.error(e?.message || "Operation failed");

      if (action === "callWaiting" && payload?.portLabel) {
        const { portLabel, nextValue } = payload;
        setP(portLabel, (s) => ({ ...s, callWaiting: !nextValue }));
      }
    } finally {
      closeConfirm();
    }
  };

  const handleCancelWarning = () => {
    const { action, payload } = confirmState;

    if (action === "callWaiting" && payload?.portLabel) {
      const { portLabel, nextValue } = payload;
      setP(portLabel, (s) => ({ ...s, callWaiting: !nextValue }));
    }

    closeConfirm();
  };
  const blockAddDisabled = (() => {
    if (!currentPort) return true;
  
    const cur = getP(currentPort);
    const nums = parseBlockedNumbers(cur.blockInput || "");
    if (nums.length === 0) return true;
    const hasInvalid = nums.some((n) => String(n).length !== 10);
    return hasInvalid;
  })();
  const hasPorts = portsMeta.length > 0;
  return (
    <>
      <div className="cf-root">
        <div className="cf-backdrop" onClick={onClose} />
        <div className="cf-card">
          <div className="cf-topbar">
            <div className="cf-header-row">
              <h2 className="cf-title">
                Call Features <span className="cf-dash">-</span>{" "}
                <span className="cf-sub">
                  {boxDetails?.serialNumber} - {boxDetails?.displayName}
                </span>
              </h2>
              <div className="cf-head-actions">
                {isPortView && (
                  <button className="cf-btn bulk" onClick={() => setShowBulkModal(true)}
                   disabled={!hasPorts}>
                    Bulk Select Ports
                  </button>
                )}
              </div>
            </div>
            {oobError && <div className="cf-error">{oobError}</div>}
            <div className="cf-tabs">
              {topTabs.map((t) => (
                <Pill key={t} active={activeTab === t} onClick={() => selectTab(t)}>
                  {t}
                </Pill>
              ))}
            </div>
            {gridPorts.length > 0 && (
              <div className="cf-portsWrap">
                <div className="cf-portsRow">
                  {gridPorts.map((p) => (
                    <button
                      key={p}
                      className={cx("cf-portCell", selectedGridPort === p && "selected")}
                      onClick={() => selectGridPort(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* <div className="cf-content"> */}
            {/* {isPortView && ( */}
          <div className="cf-content">
            {portsMeta.length === 0 ? (
              <div className="cf-empty-state">
                There are no lines currently assigned to the analog ports on this device.
              </div>
            ) : (
              isPortView && (
                  <div className="cf-port-wrap">
              <div className="cf-port-wrap">
                <div className="cf-row3">
                  <div className="cf-row3-left">
                    <span className="cf-spacer" />
                    <h1 className="cf-port-title">
                      {currentPort} - {NUMBER_TYPES_LABEL[currentPortState.selectNumber?.type] || "unassigned"} -{" "}
                      {currentPortState.selectNumber?.number || ""}
                    </h1>
                  </div>
                </div>

                <div className="cf-section">
                  {currentPortIsEscene && canViewKey("threeWayCalling") && (
                    <div className="cf-row3">
                      <div className="cf-row3-left">
                        <span className="cf-spacer" />
                        <span className="cf-section-title">3 Way Calling</span>
                      </div>
                      <div className="cf-row3-mid">
                        <Toggle
                          disabled={!canWriteKey("threeWayCalling")}
                          value={getP(currentPort).threeWayOn}
                          onToggle={async (v) => {
                            if (!canWriteKey("threeWayCalling")) return;
                            const next = !v;
                            setP(currentPort, (s) => ({
                              ...s,
                              threeWayOn: next,
                            }));
                            try {
                              await saveSimpleToggle(
                                currentPort,
                                "conferenceCall",
                                next
                              );
                            } catch (e) {
                              toast.error(
                                e?.message ||
                                  `Failed to ${
                                    next ? "enable" : "disable"
                                  } 3 Way Calling`
                              );
                              setP(currentPort, (s) => ({
                                ...s,
                                threeWayOn: !next,
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {/* CallTransferEnable */}
                  {currentPortIsEscene && canViewKey("callTransfer") && (
                    <div className="cf-row3">
                      <div className="cf-row3-left">
                        <span className="cf-spacer" />
                        <span className="cf-section-title">Call Transfer</span>
                      </div>
                      <div className="cf-row3-mid">
                        <Toggle
                          disabled={!canWriteKey("callTransfer")}
                          value={getP(currentPort).CallTransferEnable}
                          onToggle={async (v) => {
                            if (!canWriteKey("callTransfer")) return;
                            const next = !v;
                            setP(currentPort, (s) => ({
                              ...s,
                              CallTransferEnable: next,
                            }));
                            try {
                              await saveSimpleToggle(
                                currentPort,
                                "CallTransferEnable",
                                next
                              );
                            } catch (e) {
                              toast.error(
                                e?.message ||
                                  `Failed to ${
                                    next ? "enable" : "disable"
                                  } Call Transfer`
                              );
                              setP(currentPort, (s) => ({
                                ...s,
                                threeWayOn: !next,
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {currentPortState.selectNumber?.type === "voice" && canViewKey("callWaiting") && (
                  <div className="cf-row3">
                   <div className="cf-row3-left">
                    <span className="cf-spacer" />
                  <span className="cf-section-title">Call Waiting</span>
                    </div>
                    <div className="cf-row3-mid">
                      <Toggle
                           disabled={!canWriteKey("callWaiting")}
                          value={getP(currentPort).callWaiting}
                          onToggle={(v) => {
                            if (!canWriteKey("callWaiting")) return;
                            const next = !v;
                            setP(currentPort, (s) => ({ ...s, callWaiting: next }));
                         setConfirmState({
                            open: true,
                            action: "callWaiting",
                            payload: {
                              portLabel: currentPort,
                              nextValue: next,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                  )}
                  {/* {isEsceneDevice && ( */}
                  <>
                    <div className="cf-section-title">{currentPort} Call Forwarding</div>

                    {canViewKey("callForwardBusy") && (
                      <>
                      <div className="cf-row3">
                        <div className="cf-row3-left">
                          <span
                            className={cx("cf-caret", getP(currentPort).busyExpand && "open")}
                            onClick={() => {
                              if (getP(currentPort).busyOn) {
                                setP(currentPort, (s) => ({ ...s, busyExpand: !s.busyExpand }));
                             }
                            }}
                          />
                          <span className="cf-label">Call Forward Busy</span>
                          <InfoPopupCallFeature
                            id="callForwardBusy"
                            text={TOOLTIPS_TEXT.CALL_FORWARD_BUSY}
                          />
                        </div>
                        <div className="cf-row3-mid">
                          <Toggle
                             disabled={!canWriteKey("callForwardBusy")}
                           value={getP(currentPort).busyOn}
                            onToggle={async (v) => {
                                if (!canWriteKey("callForwardBusy")) return;
                                const next = !v;
                                setP(currentPort, (s) => ({
                                  ...s,
                                  busyOn: next,
                                  busyExpand: next,
                                  busyRoute: next ? s.busyRoute : "voicemail",
                                  busyPhone: next ? s.busyPhone : "",
                                }));
                                if (next) {
                                  toast.info(
                                    "Call Forward Busy toggled ON. Click Save to apply."
                                  );
                                } else {
                                  const meta = portMetaByLabel.get(currentPort);
                                  if (!meta) return;
                                  const payload = {
                                    callForwardBusy: {
                                      enabled: false,
                                      route: "voicemail",
                                      phoneNumber: "",
                                    },
                                  };
                                try {
                                    await withSaving(`${currentPort}:busy`, async () => {
                                      await updatePortFeature(
                                        token,
                                        meta.obiId,
                                        meta.boxId,
                                        meta.boxPortNumber,
                                        meta.deviceId,
                                        payload
                                      );
                                      toast.success(
                                        `Call Forward Busy disabled for ${currentPort}`
                                      );                               
                                          });
                                } catch (e) {}
                              }
                            }}
                          />
                        </div>
                        <div className="cf-row3-right">
                          <span>
                            <b>Enable</b> *90
                          </span>
                          <span>
                            <b>Disable</b> *91
                          </span>
                     </div>
                      </div>

                      {getP(currentPort).busyExpand && getP(currentPort).busyOn && (
                        <div className="cf-inline-controls">
                          <label className="cf-radio-inline">
                            <input
                              type="radio"
                                disabled={!canWriteKey("callForwardBusy")}
                              checked={getP(currentPort).busyRoute === "voicemail"}
                              onChange={() =>
                                setP(currentPort, (s) => ({
                                  ...s,
                                  busyRoute: "voicemail",
                                  busyPhone: "",
                                }))
                              }
                            />
                            Voicemail
                          </label>
                          <label className="cf-radio-inline">
                            <input
                              type="radio"
                                disabled={!canWriteKey("callForwardBusy")}
                              checked={getP(currentPort).busyRoute === "phone"}
                              onChange={() =>
                                setP(currentPort, (s) => ({
                                  ...s,
                                  busyRoute: "phone",
                                }))
                              }
                            />
                            Phone Number:
                          </label>
                          {getP(currentPort).busyRoute === "phone" && (
                            <PhoneInput
                                disabled={!canWriteKey("callForwardBusy")}
                              value={getP(currentPort).busyPhone}
                              onChange={(val) =>
                                setP(currentPort, (s) => ({
                                  ...s,
                                  busyPhone: val,
                                }))
                            } 
                            />
                          )}
                         <button
                            className={cx(
                              "cf-btn inline-save",
                              !canSaveBusy && "cf-btn-warn"
                            )}
                            disabled={!!saving[`${currentPort}:busy`] || !canWriteKey("callForwardBusy")}
                            onClick={() => {
                                if (!canWriteKey("callForwardBusy")) return;
                                if (!canSaveBusy) {
                                  toast.error("Phone number must be exactly 10 digits.");
                                  return;
                                }

                                withSaving(`${currentPort}:busy`, handleSaveBusy);
                            }}
>
                             {saving[`${currentPort}:busy`] ? "Saving…" : "Save"}
                          </button>
                        </div>
                      )}
                      </>
                    )}

                    {canViewKey("callForwardNoAnswer") && (
                    <>
                      <div className="cf-row3">
                        <div className="cf-row3-left">
                          <span
                            className={cx("cf-caret", getP(currentPort).naExpand && "open")}
                            onClick={() => {
                              if (getP(currentPort).naOn) {
                                setP(currentPort, (s) => ({ ...s, naExpand: !s.naExpand }));
                              }
                            }}
                          />
                         <span className="cf-label">Call Forward No-Answer</span>
                         <InfoPopupCallFeature id="callForwardNoAnswer" text={TOOLTIPS_TEXT.CALL_FORWARD_NO_ANSWER} />
                        </div>
                        <div className="cf-row3-mid">
                          <Toggle
                            disabled={!canWriteKey("callForwardNoAnswer")}
                            value={getP(currentPort).naOn}
                            onToggle={async (v) => {
                                if (!canWriteKey("callForwardNoAnswer")) return;
                              const next = !v;
                              setP(currentPort, (s) => ({
                                ...s,
                                naOn: next,
                                 naExpand: next,
                                 naRoute: next ? s.naRoute : "voicemail",
                              naPhone: next ? s.naPhone : "",
                                naSeconds: next ? s.naSeconds : 18,
                              }));
                              if (next) {
                                toast.info(
                                  "No-Answer forwarding toggled ON. Click Save to apply."
                                );                                } else {
                                 const meta = portMetaByLabel.get(currentPort);
                               if (!meta) return;
                                 const payload = {
                                  callForwardNoAnswer: { enabled: false, ringSeconds: 18, route: "voicemail", phoneNumber: "" },
                                };
                              try {
                                  await withSaving(`${currentPort}:noanswer`, async () => {
                                    await updatePortFeature(
                                      token,
                                      meta.obiId,
                                      meta.boxId,
                                      meta.boxPortNumber,
                                      meta.deviceId,
                                      payload
                                    );          
                                    toast.success(
                                      `No-Answer forwarding disabled for ${currentPort}`
                                    );
                                    });
                                 } catch (e) {}
                              }
                          }}
                          />
                        </div>
                        <div className="cf-row3-right">
                          <span>
                            <b>Enable</b> *92
                          </span>
                          <span>
                            <b>Disable</b> *93
                          </span>
                        </div>
                      </div>

                      {getP(currentPort).naExpand && getP(currentPort).naOn && (
                      <>
                          <div className="cf-inline">
                            <span>After ringing this line for</span>
                            <SecondsDropdown
                              disabled={!canWriteKey("callForwardNoAnswer")}
                              value={getP(currentPort).naSeconds}
                              onChange={(n) =>
                                setP(currentPort, (s) => ({
                                  ...s,
                                  naSeconds: n,
                                }))
                              }                              />
                            <span>seconds, forward unanswered calls to:</span>
                          </div>
                          <div className="cf-inline-controls">
                            <label className="cf-radio-inline">
                              <input
                                type="radio"
                                  disabled={!canWriteKey("callForwardNoAnswer")}
                                  checked={getP(currentPort).naRoute === "voicemail"}
                                  onChange={() => setP(currentPort, (s) => ({ ...s, naRoute: "voicemail", naPhone: "" }))}
                               />
                              Voicemail
                            </label>
                            <label className="cf-radio-inline">
                              <input
                                type="radio"
                                  disabled={!canWriteKey("callForwardNoAnswer")}
                                checked={getP(currentPort).naRoute === "phone"}
                                onChange={() =>
                                  setP(currentPort, (s) => ({
                                    ...s,
                                    naRoute: "phone",
                                  }))
                                }                                />
                              Phone Number:
                            </label>
                            {getP(currentPort).naRoute === "phone" && (
                              <PhoneInput
                                  disabled={!canWriteKey("callForwardNoAnswer")}
                                value={getP(currentPort).naPhone}
                                onChange={(val) => setP(currentPort, (s) => ({ ...s, naPhone: val }))}
                                />
                              )}
                                                  <button
                                className={cx("cf-btn inline-save", !canSaveNA && "cf-btn-warn")}
                              disabled={!!saving[`${currentPort}:noanswer`] || !canWriteKey("callForwardNoAnswer") || !canSaveNA}
                              onClick={() => {
                                if (!canWriteKey("callForwardNoAnswer")) return;
                                  if (!canSaveNA) {
                                    toast.error("Please enter a valid phone number (at least 10 digits).");
                                    return;
                                  }
                                  withSaving(`${currentPort}:noanswer`, handleSaveNoAnswer);
                                }}
                              >
                                {saving[`${currentPort}:noanswer`] ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </>
                      )}
                      </>
                    )}

                    {canViewKey("callForwardAlways") && (
                      <>
                        <div className="cf-row3">
                          <div className="cf-row3-left">
                            <span
                              className={cx("cf-caret", getP(currentPort).alwaysExpand && "open")}
                              onClick={() => {
                                if (getP(currentPort).alwaysOn) {
                                  setP(currentPort, (s) => ({ ...s, alwaysExpand: !s.alwaysExpand }));
                                }
                              }}
                            />
                            <span className="cf-label">Call Forward Always</span>
                            <InfoPopupCallFeature id="callForwardAlways" text={TOOLTIPS_TEXT.CALL_FORWARD_ALWAYS} />
                          </div>
                          <div className="cf-row3-mid">
                            <Toggle
                              disabled={!canWriteKey("callForwardAlways")}
                              value={getP(currentPort).alwaysOn}
                              onToggle={async (v) => {
                                if (!canWriteKey("callForwardAlways")) return;
                                const next = !v;
                                setP(currentPort, (s) => ({
                                  ...s,
                                  alwaysOn: next,
                                  alwaysExpand: next,
                                  alwaysRoute: next ? s.alwaysRoute : "voicemail",
                                  alwaysPhone: next ? s.alwaysPhone : "",
                                }));
                                if (next) {
                                  toast.info("Call Forward Always toggled ON. Click Save to apply.");
                                } else {
                                  const meta = portMetaByLabel.get(currentPort);
                                  if (!meta) return;
                                  const payload = {
                                    callForwardAlways: {
                                      enabled: false,
                                      route: "voicemail",
                                      phoneNumber: "",
                                    },   
                                  };       
                                                          try {
                                    await withSaving(`${currentPort}:always`, async () => {
                                      await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, payload);
                                      toast.success(`Call Forward Always disabled for ${currentPort}`);
                                    });
                                  } catch (e) {}
                                }
                              }}
                            />
                          </div>
                          <div className="cf-row3-right">
                            <span>
                              <b>Enable</b> *72
                            </span>
                            <span>
                              <b>Disable</b> *73
                            </span>
                          </div>
                        </div>

                        {getP(currentPort).alwaysExpand && getP(currentPort).alwaysOn && (
                          <div className="cf-inline-controls">
                            <label className="cf-radio-inline">
                              <input
                                type="radio"
                                disabled={!canWriteKey("callForwardAlways")}
                                checked={getP(currentPort).alwaysRoute === "voicemail"}
                                onChange={() => setP(currentPort, (s) => ({ ...s, alwaysRoute: "voicemail", alwaysPhone: "" }))}
                              />
                              Voicemail
                            </label>
                            <label className="cf-radio-inline">
                              <input
                                type="radio"
                                disabled={!canWriteKey("callForwardAlways")}
                                checked={getP(currentPort).alwaysRoute === "phone"}
                                onChange={() => setP(currentPort, (s) => ({ ...s, alwaysRoute: "phone" }))}
                              />
                              Phone Number:
                            </label>
                            {getP(currentPort).alwaysRoute === "phone" && (
                              <PhoneInput
                                disabled={!canWriteKey("callForwardAlways")}
                                value={getP(currentPort).alwaysPhone}
                                onChange={(val) => setP(currentPort, (s) => ({ ...s, alwaysPhone: val }))}
                              />
                            )}
                            <button
                              className={cx("cf-btn inline-save", !canSaveAlways && "cf-btn-warn")}
                              disabled={!!saving[`${currentPort}:always`] || !canWriteKey("callForwardAlways") || !canSaveAlways}
                              onClick={() => {
                                if (!canWriteKey("callForwardAlways")) return;
                                if (!canSaveAlways) {
                                  toast.error("Please enter a valid phone number (at least 10 digits).");
                                  return;
                                }
                                withSaving(`${currentPort}:always`, handleSaveAlways);
                              }}
                            >
                              {saving[`${currentPort}:always`] ? "Saving…" : "Save"}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>

                  {/* Remote Call Forwarding */}
                  {canViewKey("remoteCallForward") && (
                    <>
                      <div className="cf-row3">
                        <div className="cf-row3-left">
                          <span
                            className={cx("cf-caret", getP(currentPort).remoteExpand && "open")}
                            onClick={() => {
                              if (getP(currentPort).remoteOn) {
                                setP(currentPort, (s) => ({ ...s, remoteExpand: !s.remoteExpand }));
                              }
                            }}
                          />
                          <span className="cf-label">Remote Call Forwarding</span>
                        </div>
                        <div className="cf-row3-mid">
                          <Toggle
                            disabled={!canWriteKey("remoteCallForward")}
                            value={getP(currentPort).remoteOn}
                            onToggle={async (v) => {
                              if (!canWriteKey("remoteCallForward")) return;

                              const next = !v;
                              const meta = portMetaByLabel.get(currentPort);
                              if (!meta) return;
                              const prev = getP(currentPort);
                              const savingKey = `${currentPort}:remote`;

                              setP(currentPort, (s) => ({
                                ...s,
                                remoteOn: next,
                                remoteExpand: next,
                                remotePhone: next ? s.remotePhone : "",
                              }));

                              if (next) {
                                toast.info("Remote Call Forwarding toggled ON. Click Save to apply.");
                                return;
                              }

                              if (saving[savingKey]) return;

                              try {
                                setSaving((m) => ({ ...m, [savingKey]: true }));
                                await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, {
                                  remoteCallForwarding: { isForwarded: false, forwardNumbers: [] },
                                });
                                toast.success(`Remote Call Forwarding disabled for ${currentPort}`);
                              } catch (e) {
                                toast.error(e?.message || "Failed to disable Remote Call Forwarding");
                                setP(currentPort, () => prev);
                              } finally {
                                setSaving((m) => ({ ...m, [savingKey]: false }));
                              }
                            }}
                          />
                        </div>
                        <div className="cf-row3-right" />
                      </div>

                      {getP(currentPort).remoteExpand && getP(currentPort).remoteOn && (
                        <div className="cf-inline-controls">
                          <label className="cf-radio-inline">Phone Number:</label>
                          <PhoneInput
                            disabled={!canWriteKey("remoteCallForward")}
                            value={getP(currentPort).remotePhone}
                            onChange={(val) => setP(currentPort, (s) => ({ ...s, remotePhone: val }))}
                          />

<button
  className={cx("cf-btn inline-save", !canSaveRemote && "cf-btn-warn")}
  disabled={!!saving[`${currentPort}:remote`] || !canWriteKey("remoteCallForward")}
  onClick={() => {
    if (!canWriteKey("remoteCallForward")) return;

    if (!canSaveRemote) {
      toast.error("Please enter a valid phone number (at least 10 digits).");
      return;
    }

    handleSaveRemote();
  }}
>
  {saving[`${currentPort}:remote`] ? "Saving…" : "Save"}
</button>

                        </div>
                      )}
                    </>
                  )}

                  {/* ACR */}
                  {canViewKey("callRejection") && (
                    <div className="cf-threecol">
                      <div className="cf-threecol-left">
                        <span
                          className={cx("cf-caret", getP(currentPort).acrExpand && "open", !getP(currentPort).acrOn && "disabled")}
                          onClick={() => {
                            if (!getP(currentPort).acrOn) return;
                            setP(currentPort, (s) => ({ ...s, acrExpand: !s.acrExpand }));
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={getP(currentPort).acrExpand ? "Collapse Anonymous Call Rejection" : "Expand Anonymous Call Rejection"}
                          onKeyDown={(e) => {
                            if (!getP(currentPort).acrOn) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setP(currentPort, (s) => ({ ...s, acrExpand: !s.acrExpand }));
                            }
                          }}
                        />
                        <span className="cf-spacer" />
                        <span className="cf-threecol-title" style={{ fontWeight: 400 }}>
                          Anonymous Call Rejection
                        </span>{" "}
                        <InfoPopupCallFeature id="callRejection" text={TOOLTIPS_TEXT.ANONYMOUS_CALL_REJECTION} />
                      </div>

                      <div className="cf-threecol-mid">
                        <Toggle
                          disabled={!canWriteKey("callRejection")}
                          value={getP(currentPort).acrOn}
                          onToggle={async (v) => {
                            if (!canWriteKey("callRejection")) return;
                            const next = !v;
                            const meta = portMetaByLabel.get(currentPort);
                            if (!meta) return;

                            const prev = getP(currentPort);
                            setP(currentPort, (s) => ({ ...s, acrOn: next, acrExpand: next }));

                            try {
                              await withSaving(`${currentPort}:acr`, async () => {
                                await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, meta.deviceId, {
                                  callRejection: next,
                                });
                              });
                              toast.success(`Anonymous Call Rejection ${next ? "enabled" : "disabled"}`);
                            } catch (e) {
                              toast.error(e?.message || "Failed to update ACR");
                              setP(currentPort, () => prev);
                            }
                          }}
                        />
                      </div>

                      <div className="cf-threecol-right">
                        <span>
                          <b>Enable</b> *77
                        </span>
                        <span>
                          <b>Disable</b> *87
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Call Blocking */}
                  {canViewKey("callBlocking") && (
                    <>
                      <div className="cf-threecol">
                        <div className="cf-threecol-left">
                          <span
                            className={cx("cf-caret", getP(currentPort).blockExpand && "open", !getP(currentPort).blockOn && "disabled")}
                            onClick={() => {
                              if (!getP(currentPort).blockOn) return;
                              setP(currentPort, (s) => ({ ...s, blockExpand: !s.blockExpand }));
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={getP(currentPort).blockExpand ? "Collapse Call Blocking" : "Expand Call Blocking"}
                            onKeyDown={(e) => {
                              if (!getP(currentPort).blockOn) return;
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setP(currentPort, (s) => ({ ...s, blockExpand: !s.blockExpand }));
                              }
                            }}
                          />
                          <span className="cf-spacer" />
                          <span className="cf-threecol-title" style={{ fontWeight: 400 }}>
                            Call Blocking
                          </span>{" "}
                          <InfoPopupCallFeature id="callBlocking" text={TOOLTIPS_TEXT.CALL_BLOCKING} />
                        </div>

                        <div className="cf-threecol-mid">
                          <Toggle
                            disabled={!canWriteKey("callBlocking")}
                            value={getP(currentPort).blockOn}
                            onToggle={async (v) => {
                              if (!canWriteKey("callBlocking")) return;
                              const next = !v;
                              const prev = getP(currentPort);

                              setP(currentPort, (s) => ({ ...s, blockOn: next, blockExpand: next }));

                              if (next) {
                                toast.info("Call Blocking enabled in UI. Click Save to apply.");
                                return;
                              }

                              setP(currentPort, (s) => ({ ...s, blockInput: "", blockList: [] }));

                              try {
                                await withSaving(`${currentPort}:block`, async () => {
                                  await persistDisableCallBlocking(currentPort);
                                });
                                toast.success("Call Blocking disabled");
                              } catch (e) {
                                toast.error(e?.message || "Failed to disable Call Blocking");
                                setP(currentPort, () => prev);
                              }
                            }}
                          />
                        </div>

                        <div className="cf-threecol-right">
                          <span>
                            <b>Enable</b> *60
                          </span>
                          <span>
                            <b>Disable</b> *80
                          </span>
                        </div>
                      </div>

                      {getP(currentPort).blockExpand && (
                        <div className="cf-blocklist-wrap">
                          <div className="cf-blocklist-inputRow">
                            <input
                              className="cf-input cf-blocklist-input"
                              value={getP(currentPort).blockInput}
                              onChange={handleBlockInputChange}
                              onKeyDown={handleBlockInputKeyDown}
                              placeholder="Type number and then press Enter"
                              disabled={!canWriteKey("callBlocking")}
                            />
                            <button
                              className="cf-btn cf-add-btn"
                              type="button"
                              onClick={() => {
                                if (!canWriteKey("callBlocking")) return;
                                if (blockAddDisabled) {
                                  toast.error("Phone number must be exactly 10 digits.");
                                  return;
                                }
                                addBlockNumber();
                              }}
                              disabled={!canWriteKey("callBlocking") || blockAddDisabled}
                            >
                              <span className="cf-add-btn-plus">＋</span>
                              <span>Add</span>
                            </button>
                          </div>

                          <div className="cf-blocklist-hint">
                            Type comma-separated values and press <b>enter</b> to add to the list
                          </div>

                          {getP(currentPort).blockList.length > 0 && (
                            <div className="cf-blocklist-tags">
                              {getP(currentPort).blockList.map((num) => (
                                <span key={num} className="cf-tag-pill">
                                  <span className="cf-tag-text">{num}</span>
                                  <button
                                    className="cf-tag-remove"
                                    onClick={() => {
                                      if (!canWriteKey("callBlocking")) return;
                                      removeBlockNumber(num);
                                    }}
                                    type="button"
                                    aria-label={`Remove ${num}`}
                                    disabled={!canWriteKey("callBlocking")}
                                  >
                                    −
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="cf-blocklist-saveRow">
                            <button
                              className="cf-btn inline-save"
                              disabled={!!saving[`${currentPort}:blocksave`] || !canWriteKey("callBlocking")}
                              onClick={() => {
                                if (!canWriteKey("callBlocking")) return;
                                withSaving(`${currentPort}:blocksave`, handleSaveCallBlocking);
                              }}
                            >
                              {saving[`${currentPort}:blocksave`] ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Star Codes */}
                  <>
                    <div className="cf-section-title">{currentPort} Star Codes</div>

                    {canViewKey("doNotDisturb") && (
                      <div className="cf-row3">
                        <div className="cf-row3-left">
                          <span className="cf-spacer" /> <span className="cf-label">Do Not Disturb</span>
                          <InfoPopupCallFeature id="doNotDisturb" text={TOOLTIPS_TEXT.DO_NOT_DISTURB} />
                        </div>
                        <div className="cf-row3-mid">
                          <Toggle
                            disabled={!canWriteKey("doNotDisturb")}
                            value={getP(currentPort).dndOn}
                            onToggle={async (v) => {
                              if (!canWriteKey("doNotDisturb")) return;
                              const next = !v;
                              setP(currentPort, (s) => ({ ...s, dndOn: next }));
                              try {
                                await saveSimpleToggle(currentPort, "doNotDisturb", next);
                              } catch (e) {}
                            }}
                          />
                        </div>
                        <div className="cf-row3-right">
                          <span>
                            <b>Enable</b> *78
                          </span>
                          <span>
                            <b>Disable</b> *79
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                </div>
              </div>
              </div>
              )
            )}

          </div>
          <div className="cf-footer">
            <button className="cf-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {showBulkModal && (
          <BulkSelectPortsModal
            open={showBulkModal}
            ports={portsMeta.map(({ label }) => ({ label }))}
            defaultSelected={[currentPort]}
            lockedLabels={[currentPort]}
            onClose={() => setShowBulkModal(false)}
            onSave={(selectedPortLabels) => {
              if (!isPortView) return setShowBulkModal(false);
              const s = getP(currentPort);
              const busyData = buildBusyPayload(s);
              const naData = buildNoAnswerPayload(s);
              const alwaysData = buildAlwaysPayload(s);
              const remoteData = buildRemotePayload(s);
              const simpleData = {
                doNotDisturb: !!s.dndOn,
                conferenceCall: !!s.threeWayOn,
                callWaiting: !!s.callWaiting,
                CallTransferEnable: !!s.CallTransferEnable,
                callRejection: !!s.acrOn,
                callBlocking: {
                  enabled: !!s.blockOn,
                  numbers: s.blockOn ? (s.blockList || []).map(String).join(",") : "",
                },
              };
              const updateData = { ...busyData, ...naData, ...alwaysData, ...remoteData, ...simpleData };

              const selectedPorts = selectedPortLabels
                .map((label) => {
                  const meta = portMetaByLabel.get(label);
                  const assignedNumber = portState?.[label]?.selectNumber?.number || "";
                  return {
                    number: assignedNumber,
                    obiId: meta?.obiId,
                    boxId: meta?.boxId,
                    portNumber: label,
                    boxPortNumber: meta?.boxPortNumber,
                    macAddress: meta?.macAddress,
                    deviceId: meta?.deviceId,
                    numberId: meta?.numberId,
                  };
                })
                .filter((p) => p.obiId && p.boxId);

              setShowBulkModal(false);
              setConfirmState({
                open: true,
                action: "bulk",
                payload: { selectedPorts, updateData },
              });
            }}
          />
        )}

        <CallFeaturesWarningModal
          body={
            "This action will reset the port configuration settings and restart the EPI. During the reboot, both ports will be offline for approximately 3 minutes. Do you want to continue?"
          }
          open={confirmState.open}
          isOpen={confirmState.open}
          show={confirmState.open}
          visible={confirmState.open}
          onConfirm={handleConfirmWarning}
          onCancel={handleCancelWarning}
          onClose={handleCancelWarning}
        />
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.AppState.user,
  boxDetails: state.AppState.boxDetails.box,
});

const mapDispatchToProps = (dispatch) => ({
  getSalveboxOob: (token, bid) => dispatch(BoxesActions.getSalveboxOob(token, bid)),
  updateDeviceFeatures: (token, boxId, body) =>
    dispatch(BoxesActions.updateDeviceFeatures(token, boxId, body)),
  updatePortFeature: (token, obiId, boxId, boxPortNumber, deviceId, updateData) =>
    dispatch(BoxesActions.updatePortFeature(token, obiId, boxId, boxPortNumber, deviceId, updateData)),
  bulkUpdateCallFeaturePorts: (token, boxId, selectedPorts, updateData) =>
    dispatch(BoxesActions.bulkUpdateCallFeaturePorts(token, boxId, selectedPorts, updateData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CallFeaturesModal);
